import PropTypes from 'prop-types';
import { useSoyuzExperiment, trans } from '@spotahome/soyuz/client';
import { usePricingContext } from '@spotahome/marketplace-common/src/context/PricingContext';

import { Button } from '@spotahome/ui-library';
import ListingChipsContainer from '@spotahome/ui-library/src/tenant/components/ListingChipsContainer';
import ListingTitle from '@spotahome/ui-library/src/tenant/components/ListingTitle';

import ListingContent from '@spotahome/marketplace-common/src/components/ListingContent';

import { useLandlordInformation } from '../../context/LandlordInformationContext';

import { getRnaIdFeature } from '../../utils/legalFeaturesAdapter';

import { ListingSectionObservable } from '../ListingSection';

import { getValuePropositionChips } from './getValuePropositionChips';
import openListingValuePropositionModal from './ListingValuePropositionModal';

import './ListingValueProposition.scss';

const MAX_VISIBLE_CHIPS = 6;

const ListingValueProposition = ({
  listing,
  description = '',
  landlordDescription = '',
  roomDescription = ''
}) => {
  const { hasNoDeposit } = usePricingContext();
  const { landlordInformation } = useLandlordInformation();
  const isListingSimplificationHeaderVariant =
    useSoyuzExperiment('ListingSimplificationHeader').get('behaviour') ===
    'variant';

  const chipList = getValuePropositionChips({
    listing,
    hasNoDeposit,
    landlordInformation,
    homechecker: listing.runner,
    verificationDate: listing.verificationDate
  });

  const rnaIdFeature = getRnaIdFeature(
    listing.type,
    listing.propertyFeatures,
    listing.unitFeatures
  );

  if (!isListingSimplificationHeaderVariant) {
    return null;
  }

  return (
    <ListingSectionObservable
      section="listing-value-proposition"
      className="listing-value-proposition"
    >
      <ListingTitle role="secondary">
        {trans('listing.value.proposition.title')}
      </ListingTitle>
      {chipList.length > 0 && (
        <ListingChipsContainer
          className="listing-value-proposition__chips"
          chipClassName="listing-value-proposition_chip"
          chipList={chipList}
          maxVisibleChips={MAX_VISIBLE_CHIPS}
          showShowMoreButton={false}
        />
      )}
      {description && (
        <div className="listing-value-proposition__description-preview">
          <ListingContent
            className="listing-value-proposition__description-preview-text"
            content={description}
          />
        </div>
      )}
      <Button
        type="button"
        className="listing-value-proposition__read-more"
        color="link"
        onClick={() =>
          openListingValuePropositionModal({
            chipList,
            landlordDescription,
            listingDescription: description,
            roomDescription,
            rnaIdFeature,
            fullscreenOnMobile: true
          })
        }
      >
        {trans('common.view-more')}
      </Button>
    </ListingSectionObservable>
  );
};

ListingValueProposition.propTypes = {
  listing: PropTypes.shape({
    type: PropTypes.string,
    propertyFeatures: PropTypes.object,
    unitFeatures: PropTypes.object,
    instantBooking: PropTypes.object,
    checked: PropTypes.bool,
    bills: PropTypes.object,
    allowsPermanentContract: PropTypes.bool,
    isBestChoice: PropTypes.bool,
    plan: PropTypes.any
  }).isRequired,
  description: PropTypes.string,
  landlordDescription: PropTypes.string,
  roomDescription: PropTypes.string
};

export default ListingValueProposition;
